import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import {
  downloadRecibidaPdf,
  downloadRecibidaXml,
  type RecibidaItem,
} from "@/services/invoiceService";

import {
  formatRecibidaTotal,
  formatRecibidaTypeSeries,
  getRecibidaIvaAmount,
  getRecibidaNetAmount,
  getRecibidaTotalAmount,
  recibidasStatusIcon,
  recibidasStatusVariant,
} from "@/pages/invoices/recibidas-list-shared";

type RecibidaMobileCardProps = {
  row: RecibidaItem;
  onOpen: (id: string) => void;
};

export function RecibidaMobileCard({
  row,
  onOpen,
}: RecibidaMobileCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const Icon = row.estado ? recibidasStatusIcon(row.estado) : Clock;
  const statusLabel =
    row.estado === "AE"
      ? t("recibidas.statusAE", "Document received")
      : row.estado === "BE"
        ? t("recibidas.statusBE", "Rejected")
        : (row.estado ?? "—");

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(row.id);
        }
      }}
    >
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {row.fechaHora
            ? formatDisplayDate(row.fechaHora.slice(0, 10), {
                locale: i18n.language,
              })
            : "—"}
        </p>
        <div>
          <p className="font-medium text-foreground">
            {row.legalName?.trim() || row.rucEmisor?.trim() || "—"}
          </p>
          {row.legalName?.trim() && row.rucEmisor?.trim() ? (
            <p className="text-xs text-muted-foreground tabular-nums">
              {row.rucEmisor.trim()}
            </p>
          ) : null}
        </div>
        <p className="text-sm text-foreground">{formatRecibidaTypeSeries(row, t)}</p>
        <p
          className="text-sm capitalize text-muted-foreground"
          title={row.formaPago == null ? t("recibidas.paymentMethodDashTitle") : undefined}
        >
          {row.formaPago === "credito"
            ? t("invoices.list.paymentMethodCredito", "Credit")
            : row.formaPago === "contado"
              ? t("invoices.list.paymentMethodContado", "Spot")
              : "—"}
        </p>
        <p className="text-sm tabular-nums">{row.nro}</p>
        {row.estado ? (
          <StatusBadge
            variant={recibidasStatusVariant(row.estado)}
            className="inline-flex max-w-full"
          >
            <Icon className="h-3 w-3 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">{statusLabel}</span>
          </StatusBadge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
        <p className="text-lg font-semibold tabular-nums text-foreground">
          {formatRecibidaTotal(row.moneda || "UYU", getRecibidaTotalAmount(row))}
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <div>
            <span className="block text-[10px] uppercase tracking-wide">
              {t("recibidas.neto", "Net")}
            </span>
            {formatRecibidaTotal(row.moneda || "UYU", getRecibidaNetAmount(row))}
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-wide">
              {t("recibidas.iva", "VAT")}
            </span>
            {formatRecibidaTotal(row.moneda || "UYU", getRecibidaIvaAmount(row))}
          </div>
        </div>
        {(row.totalRetenido ?? 0) !== 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("recibidas.retenido", "Withheld")}:{" "}
            {formatRecibidaTotal(row.moneda || "UYU", row.totalRetenido ?? 0)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="transition-colors duration-150"
            onClick={() => onOpen(row.id)}
          >
            {t("recibidas.actionDetails")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="transition-colors duration-150"
            onClick={() => {
              void downloadRecibidaPdf(row.id).catch(() => {
                toast.error(t("recibidas.downloadPdfError"));
              });
            }}
          >
            {t("recibidas.actionDownloadPdf")}
          </Button>
          {row.hasRawXml ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="transition-colors duration-150"
              onClick={() => {
                void downloadRecibidaXml(row.id).catch(() => {
                  toast.error(t("recibidas.downloadXmlError"));
                });
              }}
            >
              {t("recibidas.actionViewXml")}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
